/* eslint-disable react/jsx-pascal-case */
import React, {useState, useEffect, useRef} from 'react';
import cx from 'classnames';
import {useRouter} from 'next/router';
import {useTranslations, createTranslator} from 'next-intl';
import {Grid, Row, Col} from '@/grid';
import withMainLayout from 'layouts/main';
import {SectionPaddingMdToZero} from '@/section';
import Container from '@/container';
import Link from '@/links';
import {withBoldWeight, withItalic} from '@/hocs/withFontType';
import {H2, H4, P, PNoMargin, H5} from '@/typography';
import {ListTerms, ListNumbered} from '@/ui/lists/list-icons';
import {Table, TBody, TH, TR, TD} from '@/ui/tables';
import {withMarginBottomMd, withMarginBottomXl, withMarginBottomXxl} from '@/hocs/withSpacing';
import {withVisibleDesktop993, withHiddenDesktop993} from '@/hocs/withResponsiveUtils';
import withBreakpoints from '@/hocs/withBreakpoints';
import TermsForm from '@/ui/hubspotform/terms/terms-form';
import Breadcrumbs from '@/ui/breadcrumbs';
import Dropdown from '@/form-fields/dropdown';
import Tabs from '@/ui/tabs/tabs';
import LegalHero from '@/content/legal/hero';
import useDataProtectionTabItems from '@/ui/tabs/tab-items/data-protection-tabs';
import {
  handlePrint,
  handleDropdown,
  scrollToTopOfPage,
  scrollableDropdownMob,
  setTabUrl
} from '@/ui/legal/legal.helper';
import {parseLegalRichText} from '@/ui/legal/legal-rich-text';
import {pageMessagesProps} from 'helpers/i18n';
import legal from '../legal.module.scss';
import shared from '../../../messages/en/shared.json';
import legalMessages from '../../../messages/en/legal.json';
import legalDataProtection from '../../../messages/en/legalDataProtection.json';

const seoMetas = props => {
  const seoT = createTranslator({locale: props.locale, messages: props.messages});
  return {
    title: seoT('legalDataProtection.seo.title'),
    description: seoT('legalDataProtection.seo.description'),
    canonical: seoT('legalDataProtection.seo.canonical')
  };
};

const BreadcrumbsDesktop = withVisibleDesktop993(withMarginBottomXxl('div'));
const HiddenInDesktop = withHiddenDesktop993('div');
const BoldSpan = withBoldWeight('span');
const MarginedH2 = withMarginBottomMd(H2);
const DivMarginedBottomXl = withMarginBottomXl('div');
const ItalicPNoMargin = withItalic(PNoMargin);
const ItalicP = withItalic(P);
const BoldH5 = withBoldWeight(H5);
const List = props => <ListTerms {...props} />;
const ListOl = props => <ListNumbered {...props} />;

const EXHIBIT_BLOCK_TAGS = {
  p: P,
  pNoMargin: PNoMargin,
  italicP: ItalicP,
  italicPNoMargin: ItalicPNoMargin
};

const renderExhibitBlock = (block, index) => {
  if (block.tag === 'list') {
    return (
      <ul className={legal['list-not-styled']} key={index}>
        {block.items.map(item => (
          <li key={item}>{parseLegalRichText(item)}</li>
        ))}
      </ul>
    );
  }
  if (block.tag === 'link') {
    return (
      <Link key={index} href={block.href} variant="default">
        {block.text}
      </Link>
    );
  }
  const Tag = EXHIBIT_BLOCK_TAGS[block.tag];
  return <Tag key={index}>{parseLegalRichText(block.text)}</Tag>;
};

const renderTableDetails = details =>
  details.map((block, index) => {
    const isLast = index === details.length - 1;
    if (block.tag === 'list') {
      return (
        <List key={index}>
          {block.items.map(item => (
            <li key={item}>{parseLegalRichText(item)}</li>
          ))}
        </List>
      );
    }
    return (
      <React.Fragment key={index}>
        {parseLegalRichText(block.text)}
        {!isLast && (
          <>
            <br />
            <br />
          </>
        )}
      </React.Fragment>
    );
  });

const renderItem = item => {
  if (typeof item === 'string') {
    return <li key={item}>{parseLegalRichText(item)}</li>;
  }
  return (
    <li key={item.text}>
      {parseLegalRichText(item.text)}
      <ListOl>
        {item.subItems.map(sub => (
          <li key={sub}>{parseLegalRichText(sub)}</li>
        ))}
      </ListOl>
    </li>
  );
};

const DataProtectionPage = () => {
  const router = useRouter();
  const dropdownRef = useRef();
  const [activeTab, setActiveTab] = useState('data-processing-addendum');
  const [isDropdownFixed, setIsDropdownFixed] = useState(false);
  const t = useTranslations('legalDataProtection');
  const legalT = useTranslations('legal');
  const tabItems = useDataProtectionTabItems();

  useEffect(() => {
    setTabUrl(activeTab, setActiveTab, router);
  }, []);

  useEffect(() => {
    scrollableDropdownMob(dropdownRef, setIsDropdownFixed);
  }, []);

  const ResponsiveMenu = withBreakpoints({
    desktop: () => (
      <Tabs
        activeTab={activeTab}
        title={legalT('sectionTitles.dataProtection')}
        tabItems={tabItems}
        setActiveTab={setActiveTab}
        router={router}
      />
    ),
    mobile: () => (
      <div
        ref={dropdownRef}
        className={cx(
          isDropdownFixed ? legal['legal-dropdown-fixed'] : legal['legal-dropdown'],
          'no-print'
        )}>
        <Dropdown
          name="data-protection"
          defaultValue={activeTab}
          variant="green800"
          values={tabItems}
          onChange={item => handleDropdown(item, router, setActiveTab)}
        />
      </div>
    )
  });

  const tableOfContentsDataProcessingAddendum = t.raw('dpa.tableOfContents');

  const DataProcessingAddendum = () => (
    <>
      <MarginedH2>{t('dpa.heading')}</MarginedH2>
      <DivMarginedBottomXl>
        <Link onClick={handlePrint} variant="secondary-no-gradient-download" className="no-print">
          {t('dpa.downloadLabel')}
        </Link>
      </DivMarginedBottomXl>
      <div className={cx(legal['table-of-contents'], 'no-print')}>
        <ol>
          {tableOfContentsDataProcessingAddendum.map((item, index) => (
            <li key={index}>
              <Link href={item.urlCopy} variant="green800">
                {item.title}
              </Link>
            </li>
          ))}
        </ol>
      </div>

      {t.raw('dpa.introRich').map(paragraph => (
        <P key={paragraph}>{parseLegalRichText(paragraph)}</P>
      ))}
      <ListOl className={legal['scroll-mg-t']}>
        {t.raw('dpa.sections').map(section => (
          <li id={section.id} key={section.id}>
            <H4>{section.title}</H4>
            {section.paragraph ? (
              <PNoMargin>{parseLegalRichText(section.paragraph)}</PNoMargin>
            ) : (
              <ListOl>{section.items.map(renderItem)}</ListOl>
            )}
          </li>
        ))}
      </ListOl>

      <BoldH5 className={legal['scroll-mg-t']} id="exhibit-a">
        {t('dpa.exhibitA.label')}
      </BoldH5>
      <H4>{t('dpa.exhibitA.title')}</H4>
      {t.raw('dpa.exhibitA.introRich').map(paragraph => (
        <P key={paragraph}>{parseLegalRichText(paragraph)}</P>
      ))}
      <ListOl>
        {t.raw('dpa.exhibitA.items').map(item => (
          <li key={item.title}>
            <H4>{item.title}</H4>
            {item.blocks.map(renderExhibitBlock)}
          </li>
        ))}
      </ListOl>

      <BoldH5 className={legal['scroll-mg-t']} id="exhibit-b">
        {t('dpa.exhibitB.label')}
      </BoldH5>
      <P>{parseLegalRichText(t('dpa.exhibitB.introRich'))}</P>
      <ListOl>
        {t.raw('dpa.exhibitB.items').map(item => (
          <li key={item.title}>
            <H4>{item.title}</H4>
            {item.blocks.map(renderExhibitBlock)}
          </li>
        ))}
      </ListOl>

      <BoldH5 className={legal['scroll-mg-t']} id="exhibit-c">
        {t('dpa.exhibitC.label')}
      </BoldH5>
      <H4>{t('dpa.exhibitC.title')}</H4>
      <PNoMargin>{t('dpa.exhibitC.intro')}</PNoMargin>
      <PNoMargin>
        <Link
          variant="default"
          href="/static/downloads/technical-and-organisational-security-measures.pdf?1.0 "
          target="_blank">
          {t('dpa.exhibitC.downloadLinkText')}
        </Link>
      </PNoMargin>
    </>
  );

  const SecurityMeasures = () => (
    <>
      <MarginedH2>{t('securityMeasures.heading')}</MarginedH2>
      <DivMarginedBottomXl>
        <Link onClick={handlePrint} variant="secondary-no-gradient-download" className="no-print">
          {t('securityMeasures.downloadLabel')}
        </Link>
      </DivMarginedBottomXl>
      <Table className={legal['legal-table']}>
        <TBody>
          <TR>
            <TH>{t('securityMeasures.tableHeaders.measure')}</TH>
            <TH>{t('securityMeasures.tableHeaders.details')}</TH>
          </TR>
          {t.raw('securityMeasures.rows').map(row => (
            <TR key={row.measure}>
              <TD>
                <BoldSpan>{row.measure}</BoldSpan>
              </TD>
              <TD>{renderTableDetails(row.details)}</TD>
            </TR>
          ))}
        </TBody>
      </Table>
    </>
  );

  const Ccpa = () => (
    <>
      <MarginedH2>{t('ccpa.heading')}</MarginedH2>
      <DivMarginedBottomXl>
        <Link onClick={handlePrint} variant="secondary-no-gradient-download" className="no-print">
          {t('ccpa.downloadLabel')}
        </Link>
      </DivMarginedBottomXl>
      <ListOl className={legal['numbered-p-list']}>
        {t.raw('ccpa.paragraphs').map(paragraph => (
          <li key={paragraph}>
            <PNoMargin>{paragraph}</PNoMargin>
          </li>
        ))}
      </ListOl>
    </>
  );

  const activeTabContent = () => {
    switch (activeTab) {
      case 'CCPA/CPRA':
        return <Ccpa />;
      case 'security-measures':
        return <SecurityMeasures />;
      default:
        return <DataProcessingAddendum />;
    }
  };

  return (
    <>
      <LegalHero title={legalT('sectionTitles.dataProtection')} />
      <SectionPaddingMdToZero>
        <Container>
          <Grid fluid>
            <BreadcrumbsDesktop>
              <Breadcrumbs
                clickableLink={{text: legalT('breadcrumb.legal'), urlCopy: '/legal'}}
                title={legalT('sectionTitles.dataProtection')}
                activeTab={tabItems.find(item => item.value === activeTab).text}
              />
            </BreadcrumbsDesktop>
            <Row>
              <Col xs={12} md={12} lg={3}>
                <ResponsiveMenu />
              </Col>
              <Col xs={12} md={12} lgOffset={1} lg={8}>
                {activeTabContent()}
              </Col>
            </Row>
            <TermsForm />
            <HiddenInDesktop>
              <Link variant="go-to-top-arrow" onClick={scrollToTopOfPage} />
            </HiddenInDesktop>
          </Grid>
        </Container>
      </SectionPaddingMdToZero>
    </>
  );
};

export default withMainLayout(DataProtectionPage, seoMetas);

export const messagesForIntl = {shared, legal: legalMessages, legalDataProtection};

export function getStaticProps() {
  return pageMessagesProps(messagesForIntl);
}
